/**
 * Shared API Composable
 * Provides unified API interface for all restaurant projects
 * Combines menu, orders, pricing, and special deals functionality
 * Now with MoneyUtils integration for precise cent-based calculations
 */
import { useMenu } from '@shared/composables/useMenu';
import { getCategoryIconFallbackForSlug, resolveCategoryIconFromApi, } from '@shared/config/categoryIconRegistry';
import { useOrders } from '@shared/composables/useOrders';
import { useOrderPricing } from '@shared/composables/useOrderPricing';
import { useSpecialDeals } from '@shared/composables/useSpecialDeals';
/**
 * Unified API Composable
 * Combines all restaurant-related functionality in one interface
 */
export function useApi() {
    // Use shared composables
    const menu = useMenu();
    const orders = useOrders();
    const pricing = useOrderPricing();
    const specialDeals = useSpecialDeals();
    console.log('🔄 Shared useApi() - unified API interface');
    // Legacy method implementations for backward compatibility
    const fetchProducts = async () => {
        console.log('🔄 fetchProducts() -> using shared menu.loadMenu()');
        return await menu.loadMenu();
    };
    const fetchProduct = async (id) => {
        console.log(`🔄 fetchProduct(${id}) -> using shared menu.loadMenuItem()`);
        return await menu.loadMenuItem(id);
    };
    const createOrder = async (orderData) => {
        console.log('🔄 createOrder() -> using shared orders.submitOrder()');
        return await orders.submitOrder(orderData.customer, orderData.specialInstructions);
    };
    const calculatePricing = async (items) => {
        console.log('🔄 calculatePricing() -> using shared pricing.calculateOrderPricing()');
        return await pricing.calculateOrderPricing(items);
    };
    const healthCheck = async () => {
        console.log('🔄 healthCheck() -> feature available via shared RestaurantApiClient');
        return { status: 'healthy', message: 'Using shared API client' };
    };
    return {
        // Legacy state (computed from shared composables)
        isLoading: menu.isLoading,
        hasError: menu.hasError,
        errorMessage: menu.error,
        // Legacy methods (redirected to shared)
        fetchProducts,
        fetchProduct,
        createOrder,
        calculatePricing,
        healthCheck,
        // Enhanced shared functionality
        loadMenu: menu.loadMenu,
        loadMenuItem: menu.loadMenuItem,
        loadSpecialDeals: specialDeals.loadSpecialDeals,
        loadMenuBundle: menu.loadMenuBundle,
        hydrateFromPrerenderDocument: menu.hydrateFromPrerenderDocument,
        submitOrder: orders.submitOrder,
        calculateOrderPricing: pricing.calculateOrderPricing,
        // Pricing functionality
        formatPrice: pricing.formatPrice,
        formatCurrency: pricing.formatCurrency,
        subtotal: pricing.subtotal,
        deliveryFee: pricing.deliveryFee,
        serviceFee: pricing.serviceFee,
        totalAmount: pricing.totalAmount,
        taxBreakdown: pricing.taxBreakdown,
        taxCalculation: pricing.taxCalculation,
        // State from shared composables
        menuItems: menu.menuItems,
        comboMenus: menu.comboMenus,
        categories: menu.categories,
        ingredients: menu.ingredients,
        ingredientCategories: menu.ingredientCategories,
        groupedCategories: menu.groupedCategories,
        cart: orders.cartItems,
        cartTotal: orders.cartTotal,
        cartItemCount: orders.cartItemCount,
        orderPricing: pricing.pricingData,
        formattedTotal: pricing.formattedTotal,
        specialDeals: specialDeals.deals,
        featuredDeals: specialDeals.featuredDeals,
        // Utility methods
        addToCart: orders.addToCart,
        removeFromCart: orders.removeFromCart,
        clearCart: orders.clearCart,
        getItemsByCategory: menu.getItemsByCategory,
        getItemById: menu.getItemById
    };
}
/**
 * Shared utility function for grouping products by category
 * Pure transformation logic without framework dependencies
 * Now uses full category data for complete information (description, icon, etc.)
 */
export function groupProductsByCategory(products, categories) {
    console.log('🔄 groupProductsByCategory() -> shared pure transformation logic');
    console.log('📊 Input products:', products.length);
    console.log('📊 Input categories array:', categories ? categories.length : 'not provided');
    if (categories) {
        console.log('🏷️ Available categories:', categories.map(cat => `${cat.name} (sortOrder: ${cat.sortOrder})`).join(', '));
    }
    // Create category lookup map for efficient access
    const categoryLookup = new Map();
    if (categories) {
        categories.forEach(cat => {
            // Handle different category object structures
            const categoryId = cat._id || cat.id || cat.name;
            if (categoryId) {
                categoryLookup.set(categoryId, cat);
                // Also index by name/slug for fallback matching
                if (cat.name)
                    categoryLookup.set(cat.name, cat);
                if (cat.slug)
                    categoryLookup.set(cat.slug, cat);
            }
        });
    }
    // Temporäres Map für Kategorien
    const categoryMap = new Map();
    products.forEach(product => {
        // Kategorie kann ein Objekt sein (populated) oder ein String (fallback)
        const isPopulatedCategory = typeof product.category === 'object' && product.category !== null;
        let categoryId;
        let categoryData = null;
        let categoryName;
        let categorySlug;
        let categoryDescription;
        let categoryIcon = null;
        let categoryColor = null;
        // 1. Try to get category ID first
        if (isPopulatedCategory) {
            const popCat = product.category;
            categoryId = popCat._id || popCat.id || popCat.name || 'unknown';
            categoryData = popCat;
        }
        else if (typeof product.category === 'string') {
            categoryId = product.category;
        }
        else {
            categoryId = 'unknown';
        }
        // 2. Look up full category data from categories array
        if (!categoryData && categoryLookup.has(categoryId)) {
            categoryData = categoryLookup.get(categoryId);
        }
        // 3. If still no category data, try fallback lookups
        if (!categoryData && product.categoryName) {
            const fallbackName = product.categoryName;
            if (categoryLookup.has(fallbackName)) {
                categoryData = categoryLookup.get(fallbackName);
            }
        }
        // 4. Extract category information (prioritize full category data)
        if (categoryData) {
            // ✅ Use full category data
            categoryName = categoryData.name || categoryData.categoryName || categoryId;
            categorySlug = categoryData.slug || categoryName.toLowerCase().replace(/\s+/g, '-');
            categoryDescription = categoryData.description || categoryName;
            categoryIcon = resolveCategoryIconFromApi(categoryData.icon, categoryData.slug || categorySlug || categoryId);
            categoryColor = categoryData.color || null;
        }
        else if (product.categoryName) {
            // ✅ Fallback to product's category fields
            categoryName = product.categoryName;
            categorySlug = product.categorySlug || categoryName.toLowerCase().replace(/\s+/g, '-');
            categoryDescription = categoryName; // No description available
            categoryIcon = null;
        }
        else if (isPopulatedCategory) {
            // ✅ Fallback to populated category object
            const popCat = product.category;
            categoryName = popCat.name || popCat.categoryName || categoryId;
            categorySlug = popCat.slug || categoryName.toLowerCase().replace(/\s+/g, '-');
            categoryDescription = popCat.description || categoryName;
            categoryIcon = resolveCategoryIconFromApi(popCat.icon, popCat.slug || categorySlug || categoryId);
            categoryColor = popCat.color || null;
        }
        else {
            // ✅ Final fallback to category ID
            categoryName = categoryId;
            categorySlug = categoryId.toLowerCase().replace(/\s+/g, '-');
            categoryDescription = categoryName;
            categoryIcon = null;
        }
        const mapKey = categorySlug;
        if (!categoryMap.has(mapKey)) {
            // Neue Kategorie erstellen mit vollständigen Informationen
            categoryMap.set(mapKey, {
                id: mapKey,
                name: categoryName,
                description: categoryDescription, // ✅ Now uses real description from category data
                icon: categoryIcon || getCategoryIconFallbackForSlug(categorySlug || mapKey),
                color: categoryColor || categoryData?.color || undefined,
                items: [],
                sortOrder: categoryData?.sortOrder || 0 // ✅ Store sortOrder for sorting
            });
        }
        // Produkt zur Kategorie hinzufügen
        categoryMap.get(mapKey).items.push(product);
    });
    // ✅ FIXED: Sort categories by sortOrder (if available) and then by name
    const sortedCategories = Array.from(categoryMap.values()).sort((a, b) => {
        // Use stored sortOrder from category data
        const sortA = a.sortOrder || 0;
        const sortB = b.sortOrder || 0;
        console.log(`🔢 Comparing categories: ${a.name} (sortOrder: ${sortA}) vs ${b.name} (sortOrder: ${sortB})`);
        if (sortA !== sortB) {
            return sortA - sortB;
        }
        // Fallback to name sorting
        return a.name.localeCompare(b.name);
    });
    console.log('📋 Final category order:', sortedCategories.map(cat => `${cat.name} (sortOrder: ${cat.sortOrder})`).join(', '));
    // ✅ Sort products within each category by sortOrder
    sortedCategories.forEach(category => {
        category.items.sort((a, b) => {
            const sortA = a.sortOrder || 0;
            const sortB = b.sortOrder || 0;
            if (sortA !== sortB) {
                return sortA - sortB;
            }
            // Fallback to name sorting if sortOrder is the same
            return (a.name || '').localeCompare(b.name || '');
        });
    });
    // ✅ Remove sortOrder from final output (clean Category interface)
    return sortedCategories.map(category => ({
        id: category.id,
        name: category.name,
        description: category.description,
        icon: category.icon,
        color: category.color,
        items: category.items
    }));
}
/** @deprecated Nutze getCategoryIconFallbackForSlug aus categoryIconRegistry */
export function getCategoryIcon(categoryId) {
    return getCategoryIconFallbackForSlug(categoryId);
}
